import React, { useEffect, useRef, useState } from 'react';
import type LottieWeb from 'lottie-web';
import type { AnimationItem, AnimationSegment } from 'lottie-web';
import { loadable } from '@shared/core/loadableFromNamedExport';
import { withWindow } from '@shared/utils/withWindow';

export interface LottiePlayerProps {
  loop?: boolean;
  autoplay?: boolean;
  animationProgress?: number;
  restart?: boolean;
  lottieUrl: string;
  play?: boolean;
  delay?: number;
  playSegment?: AnimationSegment;
  containerStyle?: React.CSSProperties;
  /** Fired at the end of each loop cycle (lottie-web's `loopComplete` event). */
  onLoopComplete?: () => void;
}

const Lottie = loadable.lib(
  () =>
    // Removing the magic webpackChunkName because it's interfering with
    // webpack's internal chunking algorithm.
    import('lottie-web'),
  {
    ssr: false,
  },
);

const Root: React.FC<LottiePlayerProps & { lottie: typeof LottieWeb }> = ({
  loop = false,
  autoplay = false,
  animationProgress = 0,
  restart,
  lottieUrl,
  play,
  lottie,
  delay,
  playSegment,
  containerStyle,
  onLoopComplete,
}) => {
  const animationContainer = useRef<HTMLDivElement>(null);
  const [animation, setAnimation] = useState<AnimationItem | null>(null);

  // The instance that is currently ALIVE, as opposed to the one this render was given. The creator
  // effect sets it and nulls it the instant it destroys, so the three effects below can tell the
  // difference — which they otherwise cannot. React runs an effect whose own deps changed in the
  // same commit as the creator's swap, and `animation` in that effect is still the previous
  // render's value: already destroyed, with every method on it a crash. Reaching a destroyed
  // instance needs two props changing together (`lottieUrl` with `play`, or with `onLoopComplete`),
  // which is why it survived the earlier fixes here.
  const liveAnimationRef = useRef<AnimationItem | null>(null);

  // Two separate ways this cleanup can meet a destroyed animation, and both end in the same crash:
  // `destroy()` nulls lottie's internal callback map (`_cbs`), so `removeEventListener` then throws
  // "Cannot read properties of null (reading 'loopComplete')" out of a passive effect — which React
  // escalates to the nearest error boundary, taking a chunk of the page down with it.
  //
  //  1. UNMOUNT. React runs cleanups in declaration order, so this effect MUST stay declared above
  //     the one that creates and destroys the animation; that way the listener comes off while the
  //     instance is still alive.
  //  2. A CHANGED PROP. `lottieUrl` (or `loop`/`autoplay`) changing re-runs the creator effect,
  //     which destroys the old instance and makes a new one — but THIS effect's deps don't change
  //     until the new instance reaches state a render later. Its cleanup then runs holding the old,
  //     already-destroyed one, which declaration order cannot help with. Hence the guard.
  useEffect(() => {
    if (!animation || liveAnimationRef.current !== animation || !onLoopComplete) return;
    animation.addEventListener('loopComplete', onLoopComplete);
    return () => {
      try {
        animation.removeEventListener('loopComplete', onLoopComplete);
      } catch {
        // Already destroyed by a prop change (case 2 above). Nothing to detach, and nothing to
        // report: the listener died with the instance it was attached to.
      }
    };
  }, [animation, onLoopComplete]);

  // The instance is owned by this effect and destroyed by its own cleanup, NOT read back out of
  // state. Guarding creation on `!animation` with `animation` in the deps deadlocked the component
  // on any prop change: the cleanup destroyed the instance, the effect re-ran, and `!animation` was
  // false because state still held the destroyed object — so nothing reloaded and the container
  // stayed empty for the rest of its life. State still carries the instance for the effects below,
  // it just no longer decides whether one gets made.
  useEffect(() => {
    const container = animationContainer.current;
    if (!container) {
      return;
    }
    const nextAnimation = lottie.loadAnimation({
      container,
      renderer: 'svg',
      loop,
      autoplay,
      path: lottieUrl,
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    });
    liveAnimationRef.current = nextAnimation;
    setAnimation(nextAnimation);
    return () => {
      liveAnimationRef.current = null;
      nextAnimation.destroy();
    };
  }, [animationContainer, autoplay, loop, lottieUrl, lottie]);

  useEffect(() => {
    let animationDelay: number;
    if (animation && liveAnimationRef.current === animation) {
      if (play) {
        if (delay) {
          withWindow(window => {
            animationDelay = window.setTimeout(() => {
              // Checked again at FIRE time, not just at effect time. The creator effect can destroy
              // this instance while the timeout is pending, and this effect's own cleanup (which
              // clears the timer) doesn't run until its deps change a render later — a gap the
              // timeout can fire inside.
              if (liveAnimationRef.current !== animation) {
                return;
              }
              if (playSegment) {
                animation.playSegments(playSegment, true);
              } else {
                animation.play();
              }
            }, delay);
          });
        } else if (playSegment) {
          animation.playSegments(playSegment, true);
        } else {
          animation.play();
        }
      } else if (play === false) {
        animation.pause();
      }
      if (animationProgress) {
        const totalFrames = animation.getDuration(true) - 1;
        const currentFrame = Math.round(animationProgress * totalFrames);
        animation.goToAndStop(currentFrame, true);
      }
    }

    return () => {
      withWindow(window => {
        window.clearTimeout(animationDelay);
      });
    };
  }, [animation, animationProgress, play, delay, playSegment]);

  useEffect(() => {
    if (animation && liveAnimationRef.current === animation && restart) {
      animation.goToAndPlay(1, true);
    }
  }, [restart, animation]);

  return (
    <div
      ref={animationContainer}
      style={containerStyle}
    />
  );
};

export const LottiePlayer: React.FC<LottiePlayerProps> = props => {
  return (
    <Lottie fallback={<div></div>}>
      {({ default: lottie }) => (
        <Root
          {...props}
          lottie={lottie}
        />
      )}
    </Lottie>
  );
};
